<template>
  <div class="app-checkout-summary">
    <MfaHandler @completed="order">
      <template v-for="slot in possibleChildSlotsFor('Mfa')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </MfaHandler>
    <div class="summary--header">
      <slot name="txtHeadline"><h2>Summary</h2></slot>
    </div>
    <div class="summary--body">
      <div class="customer-data card">
        <SummarySimData
          :sim-type="simType"
          :sim-activated="simActivated"
          :msisdn="msisdn"
        >
          <template v-for="slot in possibleChildSlotsFor('Substitute')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </SummarySimData>
      </div>
      <div class="customer-data card">
        <Customer
          process="substituteSim"
          :simType="simType"
          class="customer-summary-content"
        >
          <template v-for="slot in possibleChildSlotsFor('Customer')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </Customer>
      </div>
      <div class="info-box">
        <slot v-if="simType === 'E_SIM'" :name="('txtPleaseConsiderE_SIM' + (isESIMFree ? 'Free' : ''))"></slot>
        <slot v-else :name="('txtPleaseConsiderSIM_CARD' + (simActivated ? 'Activated' : ''))"></slot>
        <slot v-if="simType === 'E_SIM' && usesESIM" :name="'txtPleaseConsiderAlreadyHasE_SIM'"></slot>
      </div>
    </div>
    <Cart process="substituteSim"
          :showFreeESIMMessage="isESIMFree"
          class="card">
      <template v-for="slot in possibleChildSlotsFor('Cart')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </Cart>
    <Stepper
      :loading-next="loading"
      :show-prev="true"
      @prev="$emit('goBack')"
      @next="initiateMfaFlow()"
      :disabled-next="isMfaLoading"
    >
      <template v-for="slot in possibleChildSlotsFor('Stepper')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </Stepper>
  </div>
</template>

<script>
import Stepper from '@/components/generic/Stepper.vue'
import trackingHandler from '@/mixins/trackingHandler'
import validationFlow from '@/mixins/validators/validationFlow'
import transformElements from '@/mixins/transformElements'
import Customer from '@/components/checkout/belatedPorting/summary/Customer.vue'
import SummarySimData from '@/components/checkout/substituteSim/summary/SimData.vue'
import toastHandler from '@/mixins/toastHandler'
import MfaHandler from '@/components/process/generic/Mfa/MfaHandler.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Cart from '@/components/checkout/substituteSim/Cart.vue'

export default {
  name: 'Summary',
  components: {
    MfaHandler,
    Cart,
    SummarySimData,
    Customer,
    Stepper
  },
  data () {
    return {
      loading: false,
      confirmation: false
    }
  },
  mounted () {
    this.stopLoader()
  },
  props: {
    msisdn: {
      type: String,
      default: ''
    },
    usesESIM: {
      type: Boolean,
      default: false
    },
    isESIMFree: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    trackingHandler,
    validationFlow,
    transformElements,
    toastHandler()
  ],
  methods: {
    ...mapMutations('mfa', [
      'setMfaStatus',
      'setToken',
      'setMethod',
      'setMsisdn',
      'resetMfaState'
    ]),
    ...mapActions('mfa', [
      'initiateMfa',
      'fetchMfaInfo'
    ]),
    async initiateMfaFlow () {
      // TODO: do the initiateMfaFlow in the MfaHandler component
      if (this.isMfaCompleted) {
        this.order()
        return
      }
      this.startLoader()
      this.setMsisdn(this.msisdn)

      // Check if we still have a valid token in case the user has already started the mfa flow. This avoids calling the critical event and generate a new token.
      if (this.getToken !== '' && this.getMethod !== '') {
        try {
          await this.fetchMfaInfo()
        } catch (error) {
          this.triggerErrorTracking(error)
          this.stopLoader()
        }
      }

      // Call critical event to retrieve a new token if we don't have one or if the token is expired
      if (this.getToken === '' || this.getMethod === '' || !this.isMfaTokenValid) {
        try {
          await this.$store.dispatch('checkoutv2/substituteSim/orderSubstituteSim')
          // if mfa is deactivated, its not a critical event anymore and we can skip the mfa flow by doing the normal order
          this.redirectToSuccessPage()
        } catch (error) {
          const errorDetails = error?.networkError?.result?.errors?.[0]?.extensions?.details
          const token = errorDetails?.['X-Multifactor-Token']
          const methods = errorDetails?.available_methods

          if (token) this.setToken(token)
          if (Array.isArray(methods) && methods.length > 0) {
            this.setMethod(methods[0])
          } else {
            this.errorToast(this.getErrorMessage(error))
            this.triggerErrorTracking(error.networkError)
          }
        } finally {
          this.stopLoader()
        }
      }

      // if mfa is deactivated, we can skip the mfa flow
      if (this.getToken !== '' && this.getMethod !== '') {
        this.startLoader()
        try {
          await this.initiateMfa()
        } catch (error) {
          this.errorToast(this.getErrorMessage(error))
          this.triggerErrorTracking(error)
        } finally {
          this.stopLoader()
        }
      }
    },
    async order () {
      this.startLoader()
      try {
        await this.$store.dispatch('checkoutv2/substituteSim/orderSubstituteSim', { token: this.getToken })
        // Reset the mfa state on success
        this.resetMfaState()
        this.redirectToSuccessPage()
      } catch (error) {
        this.errorToast(this.getErrorMessage(error))
        this.triggerErrorTracking(error)
      } finally {
        this.stopLoader()
      }
      return true
    },
    redirectToSuccessPage () {
      let successUrl = ''
      if (this.simType === 'SIM_CARD') {
        successUrl = this.getSlotText('urlSimSuccess')
      } else {
        successUrl = this.getSlotText('urlESimSuccess')
      }
      window.location.href = successUrl
    }
  },
  computed: {
    ...mapGetters('mfa', [
      'getToken',
      'getMethod',
      'isMfaCompleted',
      'isMfaTokenValid',
      'isMfaLoading'
    ]),
    simType () {
      return this.$store.getters['checkoutv2/substituteSim/simType']
    },
    simActivated () {
      return this.$store.getters['checkoutv2/substituteSim/simActivated']
    }
  }
}
</script>
